<template>
  <div>
    <SiteProject
      v-if="usedProjectIdOrName && projectResponse"
      :preloaded-response="projectResponse"
      :path="$t('routes.properties')"
    />
    <SiteProperty
      v-else-if="propertyId && response"
      :preloaded-response="response"
    />
    <SiteSearch
      v-else-if="response === false && projectResponse === false"
      :params="params"
    />
    <div v-else class="flex justify-center items-center h-screen">
      <Icon
        :path="mdiLoading"
        class="text-neutral-lighter h-10 w-10 mx-auto animate-spin"
      />
      <div class="hidden">
        {{ propertyAsync.error }}
        {{ projectAsync.error }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiLoading } from '@mdi/js';

import { paramToValue, valuesMap } from '@/helpers/search';
import type {
  ProjectDocumentResponse,
  PropertyDocumentResponse,
  QueryResponse
} from '@/types/properties';

const SiteProject = defineAsyncComponent(
  () => import('@/async-components/Site/Project/index.vue')
);
const SiteProperty = defineAsyncComponent(
  () => import('@/async-components/Site/Property/index.vue')
);
const SiteSearch = defineAsyncComponent(
  () => import('@/async-components/Site/Search/index.vue')
);

const { get } = useHttpMethods();

const { createSearchParams, possibleParamsKeys } = useSearch();

const { t } = useI18n();

function tl(key: string, locale: string) {
  return t(key, null, { locale });
}

const params = useUrlParams();
const buildingsEnParam = [
  tl('paramsAliases.building', 'en'),
  tl('paramsKeys.propertySubType', 'en')
].join('-');
const buildingsEsParam = [
  tl('paramsAliases.building', 'es'),
  tl('paramsKeys.propertySubType', 'es')
].join('-');

const buildingTabsParams = [
  tl('paramsAliases.availability', 'en'),
  tl('paramsAliases.availability', 'es'),
  tl('paramsAliases.pending', 'en'),
  tl('paramsAliases.pending', 'es'),
  tl('paramsAliases.closed', 'en'),
  tl('paramsAliases.closed', 'es'),
  tl('paramsAliases.statistics', 'en'),
  tl('paramsAliases.statistics', 'es')
];

let lastProjectIdOrName: number | string | null = null;

const usedProjectIdOrName = computed<number | string | null>(() => {
  if (params.value.length === 0) {
    return lastProjectIdOrName || null;
  }

  if (
    !params.value.includes(buildingsEnParam) &&
    !params.value.includes(buildingsEsParam)
  ) {
    return null;
  }

  let lastParam = params.value[params.value.length - 1];

  if (buildingTabsParams.includes(lastParam)) {
    lastParam = params.value[params.value.length - 2];
  }

  // pure id
  if (!isNaN(+lastParam)) {
    const id = +lastParam;
    if (id > 0) {
      lastProjectIdOrName = id;
      return id;
    }
  }

  // id after A
  const id = lastParam.match(/-([a-zA-Z]?\d+)$/i)?.[1];
  if (id) {
    lastProjectIdOrName = id;
    return id;
  }

  // param-alias
  if (Object.keys(valuesMap).includes(lastParam)) {
    return null;
  }

  // param-key
  if (
    possibleParamsKeys.find(
      k => lastParam.endsWith(`-${k}`) || lastParam.endsWith(`_${k}`)
    )
  ) {
    lastProjectIdOrName = null;
    return null;
  }

  // name
  lastProjectIdOrName = lastParam;
  return lastParam;
});

// uses previous value when url changes and params is empty
let lastPropertyId: string | null = null;

const propertyId = computed<string | null>(() => {
  if (params.value.length === 0) {
    return lastPropertyId || null;
  }

  if (
    params.value.includes(buildingsEnParam) ||
    params.value.includes(buildingsEsParam)
  ) {
    return null;
  }

  const lastParam = params.value[params.value.length - 1];

  // pure id
  if (!isNaN(+lastParam) && +lastParam > 0) {
    lastPropertyId = lastParam;
    return lastParam;
  }

  // id after any letter
  const id = lastParam.match(/([a-zA-Z]\d+)$/)?.[1];
  if (id) {
    lastPropertyId = id;
    return id;
  }

  lastPropertyId = null;

  // param-alias
  if (Object.keys(valuesMap).includes(lastParam)) {
    return null;
  }

  // param-key
  if (
    possibleParamsKeys.find(
      k => lastParam.endsWith(`-${k}`) || lastParam.endsWith(`_${k}`)
    )
  ) {
    return null;
  }

  return null;
});

// const propertyAddress = computed<number | null>(() => {
//   if (params.value.length === 0) {
//     return null;
//   }

//   const lastParam = params.value[params.value.length - 1];

//   // pure id
//   if (!isNaN(+lastParam)) {
//     const id = +lastParam;
//     if (id > 0) {
//       return null;
//     }
//   }

//   // address before id
//   const address = lastParam.match(/(.*)-[a-zA-Z](\d+)$/i)?.[1];
//   if (address) {
//     return address;
//   }

//   if (lastParam.startsWith('A') && params.value.length > 1) {
//     const address = params.value[params.value.length - 2];

//     const addressKey = address.split('-').slice(-1).join('-');
//     if (address && !possibleParamsKeys.value.includes(addressKey)) {
//       return address;
//     }
//   }

//   return null;
// });

const {
  public: { apiUrl }
} = useRuntimeConfig();

async function loadProperty() {
  if (!propertyId.value) return false;

  const id = propertyId.value;
  console.log('[PROPERTY] Loading property', id);

  const url =
    apiUrl +
    '/search?' +
    Object.entries(
      createSearchParams({
        isSingle: true,
        filterBy: [`(id:=${id.toLowerCase()} || id:=${id.toUpperCase()})`],
        omitPriceFilter: true
      })
    )
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');

  const response = await get<{ data: QueryResponse<PropertyDocumentResponse> }>(
    url
  );

  if (!response?.data?.hits?.[0]) {
    return false;
  }

  return response.data;
}

async function loadProject() {
  if (!usedProjectIdOrName.value) return false;

  const idOrName = paramToValue(usedProjectIdOrName.value);

  console.log('[BUY] Loading project', idOrName);

  const url =
    apiUrl +
    '/search/projects?' +
    Object.entries(
      createSearchParams({
        isSingle: true,
        collection: 'projects',
        filterBy: [
          `id:=${idOrName.toLowerCase()} || id:=${idOrName.toUpperCase()} || projectName:=${idOrName}`
        ]
      })
    )
      .map(vs => new URLSearchParams({ [vs[0]]: vs[1] }))
      .join('&');

  const response = await get<{ data: QueryResponse<ProjectDocumentResponse> }>(
    url
  );

  if (!response?.data?.hits?.[0]) {
    return false;
  }

  return response.data;
}

// Use 'false' and 'undefined' as string values to avoid duplication issues

const propertyAsync = await useAsyncData(
  'property-data',
  async () => {
    return (await loadProperty()) || ('false' as const);
  },
  {
    immediate: false,
    default: () => 'undefined' as const
  }
);

if (!process.client) {
  await propertyAsync.execute();
}

if (propertyAsync.error.value) {
  console.error(propertyAsync.error.value);
}

const projectAsync = await useAsyncData(
  'used-project-data',
  async () => {
    return (await loadProject()) || ('false' as const);
  },
  {
    immediate: false,
    default: () => 'undefined' as const
  }
);

if (!process.client) {
  await projectAsync.execute();
}

if (projectAsync.error.value) {
  console.error(projectAsync.error.value);
}

const response = computed(() => {
  if (propertyAsync.data.value === 'undefined') {
    return undefined;
  }
  if (propertyAsync.data.value === 'false') {
    return false;
  }
  return propertyAsync.data.value;
});

const projectResponse = computed(() => {
  if (projectAsync.data.value === 'undefined') {
    return undefined;
  }
  if (projectAsync.data.value === 'false') {
    return false;
  }
  return projectAsync.data.value;
});

onMounted(() => {
  if (propertyAsync.data.value === 'undefined') {
    propertyAsync.execute();
  }
  if (projectAsync.data.value === 'undefined') {
    projectAsync.execute();
  }
});
</script>
